import { apiRequest } from '../../../shared/services/apiClient';
import {
  type DashboardBucket,
  type DashboardRange,
} from '../utils/dashboardMetrics';

export type { DashboardRange } from '../utils/dashboardMetrics';

type BreakdownItem = {
  label: string;
  count: number;
};

export type DashboardModuleResult<T> = {
  data?: T;
  error?: string;
};

export type DashboardTaskAssigneeFilter = 'all' | 'mine' | 'unassigned';
export type DashboardConversationChannelFilter = 'all' | 'whatsApp' | 'telegram' | 'instagram';

export type DashboardSnapshotFilters = {
  taskAssignee: DashboardTaskAssigneeFilter;
  taskAssigneeId?: string;
  conversationChannel: DashboardConversationChannelFilter;
  consultationTypeId?: string;
};

export type DashboardActivityPoint = {
  key: string;
  tasks: number;
  conversations: number;
  consultations: number;
  total: number;
};

export type TaskDashboardData = {
  totalCount: number;
  completedCount: number;
  overdueCount: number;
  assignedCount: number;
  highPriorityCount: number;
  statusBreakdown: BreakdownItem[];
  priorityBreakdown: BreakdownItem[];
  activity: DashboardBucket[];
};

export type ConversationDashboardData = {
  totalCount: number;
  directCount: number;
  channelCount: number;
  externalCount: number;
  whatsAppCount: number;
  aiMutedCount: number;
  kindBreakdown: BreakdownItem[];
  activity: DashboardBucket[];
};

export type ConsultationDashboardData = {
  totalCount: number;
  completedCount: number;
  upcomingCount: number;
  withStatusCount: number;
  aiCreatedCount: number;
  statusBreakdown: BreakdownItem[];
  activity: DashboardBucket[];
};

export type DashboardSnapshot = {
  generatedAt: string;
  range: DashboardRange;
  activity: DashboardActivityPoint[];
  tasks?: DashboardModuleResult<TaskDashboardData>;
  conversations?: DashboardModuleResult<ConversationDashboardData>;
  consultations?: DashboardModuleResult<ConsultationDashboardData>;
};

export type LoadDashboardSnapshotOptions = {
  range: DashboardRange;
  canViewTasks: boolean;
  canViewConversations: boolean;
  canViewConsultations: boolean;
  filters: DashboardSnapshotFilters;
};

type DashboardSnapshotApiResponse = {
  generatedAt: string;
  range: DashboardRange;
  activity: DashboardActivityPoint[];
  tasks?: TaskDashboardData | null;
  conversations?: ConversationDashboardData | null;
  consultations?: ConsultationDashboardData | null;
};

export const loadDashboardSnapshot = async ({
  range,
  canViewTasks,
  canViewConversations,
  canViewConsultations,
  filters,
}: LoadDashboardSnapshotOptions): Promise<DashboardSnapshot> => {
  const params = new URLSearchParams({ range });

  if (filters.taskAssignee === 'mine' && filters.taskAssigneeId) {
    params.set('taskAssigneeId', filters.taskAssigneeId);
  }

  if (filters.taskAssignee === 'unassigned') {
    params.set('taskUnassignedOnly', 'true');
  }

  if (filters.conversationChannel !== 'all') {
    params.set('conversationChannel', filters.conversationChannel === 'whatsApp' ? 'whatsapp' : filters.conversationChannel);
  }

  if (filters.consultationTypeId?.trim()) {
    params.set('consultationTypeId', filters.consultationTypeId.trim());
  }

  const response = await apiRequest<DashboardSnapshotApiResponse>(`/api/v1/dashboard/snapshot?${params.toString()}`);

  return {
    generatedAt: response.generatedAt,
    range: response.range,
    activity: Array.isArray(response.activity) ? response.activity : [],
    tasks: canViewTasks && response.tasks ? { data: response.tasks } : undefined,
    conversations: canViewConversations && response.conversations ? { data: response.conversations } : undefined,
    consultations: canViewConsultations && response.consultations ? { data: response.consultations } : undefined,
  };
};